import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { BodyText } from '@calendly/ui/components/typography'
import { useCallback, useState } from 'react'

import { VerticalSortableContext } from 'components/core/sortable'

import { CustomQuestionCard, snapshotQuestionValues } from './custom_question_card'
import {
  generateBlankQuestion,
  getEditingQuestionFieldNames,
  MAX_CUSTOM_QUESTIONS,
  NAME_EDITING_KEY,
  reorderQuestions,
  snapshotNameFormatValue,
} from './helpers'
import { NameQuestionEditor } from './name_question_editor'
import { NewQuestionButton } from './new_question_button'
import { questionListCss } from './styles'
import { SystemQuestionRow } from './system_question_row'
import { validateEditingFields } from '../confirmation/validate_editing_fields'

export const CustomQuestionsSection = () => {
  const {
    values: { inviteeQuestions, nameFormat },
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormContext()
  const [editingKey, setEditingKey] = useState(null)
  const [draftSnapshot, setDraftSnapshot] = useState(null)

  const undeletedQuestions = inviteeQuestions.filter(({ _destroy }) => !_destroy)
  const isEditing = editingKey !== null

  const finishEditing = useCallback(() => {
    setDraftSnapshot(null)
    setEditingKey(null)
  }, [])

  const startEditing = useCallback((key, snapshot) => {
    setDraftSnapshot(snapshot)
    setEditingKey(key)
  }, [])

  const validateCurrentEditingQuestion = useCallback(async () => {
    if (!isEditing || editingKey === NAME_EDITING_KEY) return true

    const editingIndex = inviteeQuestions.findIndex(({ id }) => id === editingKey)
    if (editingIndex === -1) return true

    const hasErrors = await validateEditingFields({
      fieldNames: getEditingQuestionFieldNames(
        editingIndex,
        inviteeQuestions[editingIndex].format,
      ),
      setFieldTouched,
      validateForm,
    })

    return !hasErrors
  }, [editingKey, inviteeQuestions, isEditing, setFieldTouched, validateForm])

  const handleReorder = (items) => {
    setFieldValue('inviteeQuestions', reorderQuestions(inviteeQuestions, items))
    setFieldTouched('inviteeQuestions', true)
  }

  const handleNameEdit = () => {
    startEditing(NAME_EDITING_KEY, snapshotNameFormatValue(nameFormat))
  }

  const handleNameCancel = () => {
    if (draftSnapshot != null) {
      setFieldValue('nameFormat', draftSnapshot)
    }
    finishEditing()
  }

  const handleQuestionEdit = (question) => {
    startEditing(question.id, snapshotQuestionValues(question))
  }

  const handleQuestionCancel = (index, question) => {
    const isNewQuestion = String(question.id).startsWith('new')

    if (isNewQuestion) {
      const nextQuestions = inviteeQuestions
        .filter((_, questionIndex) => questionIndex !== index)
        .map((tempQuestion, newIndex) => ({ ...tempQuestion, position: newIndex }))
      setFieldValue('inviteeQuestions', nextQuestions)
      setFieldTouched('inviteeQuestions', true)
    } else if (draftSnapshot) {
      setFieldValue(`inviteeQuestions.${index}.name`, draftSnapshot.name)
      setFieldValue(`inviteeQuestions.${index}.format`, draftSnapshot.format)
      setFieldValue(`inviteeQuestions.${index}.required`, draftSnapshot.required)
      setFieldValue(`inviteeQuestions.${index}.enabled`, draftSnapshot.enabled)
      setFieldValue(`inviteeQuestions.${index}.answerChoices`, draftSnapshot.answerChoices)
      setFieldValue(`inviteeQuestions.${index}.include_other`, draftSnapshot.include_other)
    }

    finishEditing()
  }

  const deleteQuestion = (index) => {
    const removedQuestion = inviteeQuestions[index]
    const tempQuestions = inviteeQuestions
      .filter((_, questionIndex) => questionIndex !== index)
      .map((tempQuestion, newIndex) => ({ ...tempQuestion, position: newIndex }))
    const isNewQuestion = String(removedQuestion.id).startsWith('new')

    if (isNewQuestion) {
      setFieldValue('inviteeQuestions', tempQuestions)
    } else {
      setFieldValue('inviteeQuestions', [...tempQuestions, {
        ...removedQuestion,
        format: 'destroy',
        enabled: false,
        _destroy: true,
      }])
    }

    setFieldTouched('inviteeQuestions', true)

    if (editingKey === removedQuestion.id) {
      finishEditing()
    }
  }

  const handleAddQuestion = async (format = 'string') => {
    const canAdd = await validateCurrentEditingQuestion()
    if (!canAdd) return

    const newQuestion = generateBlankQuestion(undeletedQuestions.length, format)
    setFieldValue('inviteeQuestions', [...inviteeQuestions, newQuestion])
    setFieldTouched('inviteeQuestions', true)
    startEditing(newQuestion.id, snapshotQuestionValues(newQuestion))
  }

  const isNameEditing = editingKey === NAME_EDITING_KEY

  return (
    <div className={questionListCss} data-testid="custom-questions-section">
      {isNameEditing ? (
        <NameQuestionEditor
          draftSnapshot={draftSnapshot}
          onCancel={handleNameCancel}
          onDone={finishEditing}
        />
      ) : (
        <SystemQuestionRow
          disabled={isEditing}
          label={I18n.t('publisher.event_type_editor_crud.booking_page.name_label')}
          onClick={handleNameEdit}
        />
      )}
      <SystemQuestionRow
        label={I18n.t('publisher.event_type_editor_crud.booking_page.email_label')}
      />
      <VerticalSortableContext items={undeletedQuestions} setItems={handleReorder}>
        {inviteeQuestions.map((question, index) => {
          if (question._destroy) return null

          return (
            <CustomQuestionCard
              key={question.id}
              disabled={isEditing}
              editingKey={editingKey}
              index={index}
              question={question}
              onCancel={() => handleQuestionCancel(index, question)}
              onDelete={() => deleteQuestion(index)}
              onDone={finishEditing}
              onEdit={() => handleQuestionEdit(question)}
            />
          )
        })}
      </VerticalSortableContext>
      {undeletedQuestions.length < MAX_CUSTOM_QUESTIONS ? (
        <NewQuestionButton
          onAddQuestion={handleAddQuestion}
          onBeforeOpen={validateCurrentEditingQuestion}
        />
      ) : (
        <BodyText color="muted">
          {I18n.t('publisher.event_types.invitee_questions.reached_question_limit')}
        </BodyText>
      )}
    </div>
  )
}
